/*
 * The secondary views (template fns the app routes to): Map, Alerts, Rollouts,
 * Releases. The overview is its own stateful element (overview.ts). Clickable rows
 * dispatch the same `gateway-open` event the cards/map use, so the app opens the
 * detail panel from one handler regardless of source.
 *
 * Map = MapLibre GL + deck.gl. Alerts = REAL (derived from status/revoked). Rollouts
 * + Releases lean on softwareVersion (real) for version distribution; rollout
 * campaigns / release manifests are stubbed (wired up later).
 */
import { html, type TemplateResult } from 'lit';
// NOTE: <vitesse-world-map> is NOT imported here — it pulls the heavy maplibre + deck.gl
// stack. It's lazy-loaded by the app (app.ts loadMap) only when the Map view opens, so
// the app shell boots light. mapView() just renders the tag; registration is on demand.
import { DASH, relativeTime, recomputeStats } from '../core/fleet-model';
import { ft } from '../core/i18n';
import type { CloudServer, FleetGateway } from '../core/fleet-client';
import type { AlertItem } from '../core/alerts-manager';

/** Dispatch the shared open-panel event from the clicked element. */
function emitOpen(e: Event, fingerprint: string): void {
  e.currentTarget?.dispatchEvent(
    new CustomEvent('gateway-open', { detail: { fingerprint }, bubbles: true, composed: true }),
  );
}

function viewHead(title: string, sub: string): TemplateResult {
  return html`<div class="fleet-view-head">
    <h1 class="fleet-view-title">${title}</h1>
    <div class="fleet-view-sub">${sub}</div>
  </div>`;
}

/* ── Map ── full-bleed map, no title chrome, no side panel (the legend overlay
   on the map carries the high-level counts). Clicking a marker opens the detail
   drawer (handled app-side). */
export function mapView(
  gateways: FleetGateway[],
  orgNames: Map<string, string>,
  cloudServers: CloudServer[] = [],
  selectedFp = '',
): TemplateResult {
  return html`
    <div class="fleet-map-view">
      <vitesse-world-map
        .gateways=${gateways}
        .orgNames=${orgNames}
        .cloudServers=${cloudServers}
        selectedFp=${selectedFp}
      ></vitesse-world-map>
    </div>
  `;
}

/* ── Alerts — the LIVE fleet audit log + security feed (AlertsManager).
   Lifecycle transitions (online/offline/claim/unclaim/revoke/remove) + edge-IDS
   alerts, one severity-tagged timeline. Rows open the gateway; severity colours the
   icon, the event kind picks it. The bell (header) shows the same feed, dismissible. */
const KIND_ICON: Record<string, string> = {
  online: 'check',
  offline: 'unplug',
  claimed: 'shield-check',
  unclaimed: 'hard-drive',
  revoked: 'shield-alert',
  removed: 'trash-2',
  enrolled: 'hard-drive',
  version: 'package',
  ids_alert: 'shield-alert',
  ids_block: 'shield-alert',
};
const SEV_CLASS: Record<string, string> = { critical: 'red', warning: 'amber', info: 'blue' };

export function alertsView(items: AlertItem[], error = ''): TemplateResult {
  const crit = items.filter((a) => a.severity === 'critical').length;
  const warn = items.filter((a) => a.severity === 'warning').length;
  return html`
    <div class="fleet-view">
      ${viewHead(ft('view.alertsTitle'), ft('view.alertsSub', { crit, warn }))}
      ${items.length === 0
        ? html`<div class="fleet-card fleet-empty">
            <vitesse-icon name="check" size="20"></vitesse-icon> ${error || ft('view.noAlerts')}
          </div>`
        : html`<div class="fleet-rows">
            ${items.map((a) => {
              const meta = [a.detail, a.gatewayId ? `${ft('dialog.gateway')} ${a.gatewayId.slice(0, 8)}` : '', relativeTime(a.ts)]
                .filter(Boolean)
                .join(' · ');
              return html`<div
                class="fleet-card fleet-alert"
                role="button"
                tabindex="0"
                @click=${(e: Event) => a.gatewayId && emitOpen(e, a.gatewayId)}
              >
                <span class="fleet-alert-icon fleet-alert-icon--${SEV_CLASS[a.severity] ?? 'blue'}">
                  <vitesse-icon name=${KIND_ICON[a.kind] ?? 'bell'} size="15"></vitesse-icon>
                </span>
                <div class="fleet-alert-text">
                  <div class="fleet-alert-title">${a.title}</div>
                  <div class="fleet-alert-body">${meta}</div>
                </div>
              </div>`;
            })}
          </div>`}
    </div>
  `;
}

/* ── Rollouts (version distribution real; campaigns stubbed) ── */
export function rolloutsView(gateways: FleetGateway[]): TemplateResult {
  const byVer = new Map<string, number>();
  for (const g of gateways) {
    const v = g.softwareVersion || 'unknown';
    byVer.set(v, (byVer.get(v) ?? 0) + 1);
  }
  const versions = [...byVer.entries()].sort((a, b) => b[1] - a[1]);
  const total = gateways.length || 1;
  return html`
    <div class="fleet-view">
      ${viewHead(ft('view.rolloutsTitle'), ft('view.rolloutsSub'))}
      <div class="fleet-card fleet-stub">
        <vitesse-icon name="rocket" size="16"></vitesse-icon>
        <div>
          <div class="fleet-stub-title">${ft('stub.rolloutTitle')}</div>
          <div class="fleet-stub-sub">${ft('stub.rolloutBody')}</div>
        </div>
      </div>
      <div class="fleet-eyebrow">${ft('view.versionDist')}</div>
      <div class="fleet-card fleet-bars">
        ${versions.length === 0
          ? html`<div class="fleet-empty-inline">${ft('stub.noGwYet')}</div>`
          : versions.map(([v, n]) => {
              const pct = Math.round((n / total) * 100);
              return html`<div class="fleet-bar-row">
                <span class="fleet-bar-label">${v === 'unknown' ? DASH : v}</span>
                <span class="fleet-bar-track"><span class="fleet-bar-fill" style="width:${pct}%"></span></span>
                <span class="fleet-bar-count">${n} · ${pct}%</span>
              </div>`;
            })}
      </div>
    </div>
  `;
}

/* ── Releases (stub; shows real versions in the field) ── */
export function releasesView(gateways: FleetGateway[]): TemplateResult {
  const versions = [...new Set(gateways.map((g) => g.softwareVersion).filter(Boolean))].sort();
  const stats = recomputeStats(gateways);
  return html`
    <div class="fleet-view">
      ${viewHead(ft('view.releasesTitle'), ft('view.releasesSub'))}
      <div class="fleet-card fleet-stub">
        <vitesse-icon name="package" size="16"></vitesse-icon>
        <div>
          <div class="fleet-stub-title">${ft('stub.releaseTitle')}</div>
          <div class="fleet-stub-sub">${ft('stub.releaseBody')}</div>
        </div>
      </div>
      <div class="fleet-eyebrow">${ft('view.inTheField')}</div>
      <div class="fleet-card fleet-bars">
        <div class="fleet-bar-row">
          <span class="fleet-bar-label">${ft('stub.backendVersions')}</span>
          <span class="fleet-bar-count">${versions.length ? versions.join(', ') : DASH}</span>
        </div>
        <div class="fleet-bar-row">
          <span class="fleet-bar-label">${ft('stub.gateways')}</span>
          <span class="fleet-bar-count">${stats.total} (${stats.online} online)</span>
        </div>
      </div>
    </div>
  `;
}